import React, { useEffect, useState } from "react"
import Modal from "~app/Modal"
import useRedemptionType from "~hooks/useRedemptionType"
import RedemptionResponse from "~components/redemption/RedemptionResponse"
import { toast } from "react-toastify"
import useRedeemLogic, { FORM_TYPES, FormProps } from "~hooks/useRedeemLogic"
import { PerkShape } from "~shapes/perk"
import { FormProvider, useForm } from "react-hook-form"
import DynamicRedemption from "~components/redemption/DynamicRedemption"
import ShippingRedemption from "~components/redemption/ShippingRedemption"
import {
  getObjectValues,
  sanitizeObjectAttributes,
  trimObject,
} from "~utils/utils"
import moment from "moment"
import { useMutation } from "react-query"
import UserService from "~services/UserService"
import { PostUserShape } from "~shapes/user"
import { useQueryClient } from "react-query"
import { PERKS_CODE } from "~constants/perks"

export enum COMPANY_CODE {
  CCL = "carnival-cruise-line",
  FARADAY = "ffai",
}

export enum DYNAMIC_FORM_TYPES {
  CRUISE = "cruise_details",
}

interface Props {
  isOpened: boolean
  perk?: PerkShape
  userId: string
  closeModal: (v: boolean) => void
}

const RedeemModal = ({ isOpened, perk, userId, closeModal }: Props) => {
  const [successfullyRedeemed, setSuccessfullyRedeemed] =
    useState<boolean>(false)
  const [response, setResponse] = useState()
  const [completeForm, setCompleteForm] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [formResponse, setFormResponse] = useState<FormProps>()
  const form = useForm()
  const queryClient = useQueryClient()

  const perkId = perk?.id || ""
  const {
    data: redemptionType,
    refetch: fetchRedemptionType,
    isFetching: isFetchingRedemptionType,
  } = useRedemptionType(perkId)
  const { startRedeemProcess, isStartingProcess, redeemAction } =
    useRedeemLogic(perk, redemptionType?.type, userId)

  const { mutateAsync: userMutation } = useMutation(
    (payload: Partial<PostUserShape>) => UserService.patchUser(payload),
  )

  const confirmShippingAddress = async (data: any) => {
    if (!perk) return
    setIsLoading(true)
    const sanitizedData = sanitizeObjectAttributes(trimObject(data))
    const user: any = queryClient.getQueryData(["user", userId])
    const userPayload: Partial<PostUserShape> = {
      id: userId,
      profile: user?.profile,
      shippingAddress: sanitizedData,
    }
    await userMutation(userPayload)

    const response = await redeemAction(perk.id, {
      userId: userId,
      shippingAddress: sanitizedData,
    })
    await onRedemptionResponse(response)
  }

  const confirmDynamicForm = async (data: any) => {
    if (!perk) return
    let response
    ;((data = trimObject(data)),
      (data.eventDate = moment(new Date(data.eventDate)).format(
        "YYYY-MM-DD hh:mm:ss",
      )))

    data = getObjectValues(data)

    if (perk.company?.code === COMPANY_CODE.CCL) {
      const cruiseDate = moment(data.eventDate, "YYYY-MM-DD hh:mm:ss").startOf(
        "day",
      )
      const maxDepartureDate = moment.utc("2026-12-31").startOf("day")
      if (cruiseDate.isAfter(maxDepartureDate)) {
        toast.error("Cruise date must be current year")
        setIsLoading(false)
        return
      }
    }

    if (formResponse?.formId === DYNAMIC_FORM_TYPES.CRUISE) {
      const today = moment()
      const cruiseData = moment(data.eventDate, "YYYY-MM-DD")
      const weeksDiff = cruiseData.diff(today, "weeks")
      if (weeksDiff <= 4) {
        if (
          confirm(
            "Benefit requests are required to be made 4 weeks or more before the sail date, however, we will still endeavor to process this request in time for your cruise.",
          )
        ) {
          response = await redeemAction(perk.id, { userId: userId, data })
        }
      }
    } else {
      response = await redeemAction(perk.id, { userId: userId, data })
    }
    await onRedemptionResponse(response)
  }

  const handleCloseModal = async () => {
    closeModal(successfullyRedeemed)
  }

  useEffect(() => {
    if (!isOpened || !perkId) return
    setSuccessfullyRedeemed(false)
    setResponse(undefined)
    setCompleteForm(false)
    setIsLoading(false)
    setFormResponse(undefined)
    form.reset()
    ;(async () => {
      await fetchRedemptionType()
    })()
  }, [isOpened, perkId])

  const onRedemptionResponse = async (resp: any) => {
    if (!resp || (resp.status !== 200 && resp.status !== 204)) {
      await handleCloseModal()
      toast.error(`Failed to redeem`)
    } else {
      setResponse(resp.data)
      setSuccessfullyRedeemed(true)
    }
    setIsLoading(false)
  }

  useEffect(() => {
    if (!isOpened) return
    const processRedeem = async () => {
      await startRedeemProcess().then(async resp => {
        if (!!resp?.type) {
          setCompleteForm(true)
          setFormResponse(resp)
        } else {
          await onRedemptionResponse(resp)
        }
      })
    }
    if (!!redemptionType?.type) {
      ;(async () => {
        await processRedeem()
      })()
    }
  }, [redemptionType, isOpened])

  return (
    <FormProvider {...form}>
      <Modal
        scrollable={true}
        maxWidth={"500px"}
        isOpen={isOpened}
        buttonLabel={completeForm && !successfullyRedeemed ? "Redeem" : "Close"}
        onRequestClose={() => {}}
        onRequestConfirm={async () => {
          if (completeForm && !successfullyRedeemed) {
            form.trigger().then(() => {
              form.handleSubmit(async formData => {
                if (formResponse?.type === FORM_TYPES.SHIPPABLE) {
                  await confirmShippingAddress(formData)
                } else {
                  await confirmDynamicForm(formData)
                }
                return
              })()
            })
          } else {
            await handleCloseModal()
          }
        }}
        cancelButton={true}
        onRequestCancel={handleCloseModal}
        isButtonLoading={isFetchingRedemptionType || isStartingProcess}
      >
        <div style={container}>
          {isStartingProcess || isLoading ? (
            <span>Loading...</span>
          ) : (
            <>
              {!!redemptionType && successfullyRedeemed && (
                <>
                  <div style={{ alignItems: "center" }}>
                    <h2>Successfully redeemed 🎉</h2>
                  </div>
                  <RedemptionResponse
                    redemptionType={redemptionType.type}
                    response={response}
                  />
                </>
              )}

              {!successfullyRedeemed &&
                !!formResponse &&
                completeForm &&
                (formResponse?.type === FORM_TYPES.DYNAMIC ? (
                  <DynamicRedemption formId={formResponse.formId} />
                ) : (
                  <ShippingRedemption userId={userId} />
                ))}
            </>
          )}
        </div>
      </Modal>
    </FormProvider>
  )
}

export default RedeemModal

const container: React.CSSProperties = {
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  marginBottom: 10,
  padding: 5,
}
